import jsPDF from "jspdf";
import { SearchEnvHandoff } from "../../api/searchEnvs";

// Render the structured handoff JSON into the two artifact formats
// the paim user hands off to the customer:
//
//   - ``renderHandoffText`` — plain-text suitable for pasting into
//     an email body. Wide column (80 chars) so it flows in most
//     clients. No HTML / Markdown — pure ASCII so it survives
//     Outlook + plain-text replies.
//
//   - ``renderHandoffPdf``  — multi-page PDF download. Same content,
//     monospace for code snippets, page numbering on every page,
//     header on every page so it's still identifiable if printed
//     and shuffled.
//
// Both consume the same JSON; presentation choices live here, not in
// the operator. Changes here don't touch the operator side.

// --- text -----------------------------------------------------------

function fmtTime(ms: number): string {
  return new Date(ms).toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
}

function rule(): string {
  return "─".repeat(72);
}

export function renderHandoffText(h: SearchEnvHandoff): string {
  const lines: string[] = [];
  lines.push("Your TVEyes Search Environment");
  lines.push(rule());
  lines.push("");
  lines.push(`Environment id: ${h.env_id}`);
  if (h.doc_count !== null)
    lines.push(`Documents:      ${h.doc_count.toLocaleString()}`);
  if (h.provisioned_at)
    lines.push(`Created:        ${fmtTime(h.provisioned_at)}`);
  lines.push(`Available until ${fmtTime(h.expires_at)}.`);
  lines.push("");

  // Credentials block first — used by both Browser and API below.
  lines.push("CREDENTIALS");
  lines.push(rule());
  lines.push("The same user + password works for both the browser and the API.");
  lines.push("");
  lines.push(`  User:     ${h.customer_credentials.user}`);
  lines.push(`  Password: ${h.customer_credentials.password}`);
  lines.push("");

  lines.push("BROWSER ACCESS");
  lines.push(rule());
  lines.push("Open in any modern browser, then log in with the credentials above:");
  lines.push(`  ${h.dashboards.url}`);
  lines.push("");
  lines.push(h.dashboards.first_login_note);
  lines.push("");

  if (h.mcp) {
    lines.push("MCP ACCESS");
    lines.push(rule());
    lines.push(`Endpoint:  ${h.mcp.endpoint_url}`);
    lines.push(`Transport: ${h.mcp.transport}`);
    lines.push(`Auth:      ${h.mcp.auth}`);
    lines.push("");
    lines.push(h.mcp.note);
    lines.push("");
  }

  lines.push("API ACCESS");
  lines.push(rule());
  lines.push(`Endpoint:  ${h.api.endpoint_url}`);
  lines.push(`Index:     ${h.api.index_name}`);
  lines.push(`Auth:      ${h.api.auth}`);
  lines.push("");
  lines.push("Example requests (replace <password> with the password above):");
  lines.push("");
  h.sample_queries.forEach((q, i) => {
    lines.push(`  ${i + 1}. ${q.title}`);
    lines.push("");
    q.curl.split("\n").forEach((l) => lines.push(`     ${l}`));
    lines.push("");
    lines.push(`     ${q.description}`);
    lines.push("");
  });

  lines.push("COMPATIBLE CLIENTS");
  lines.push(rule());
  h.compatible_clients.forEach((c) => {
    lines.push(`  ${c.name} (${c.language})`);
    lines.push(`    ${c.url}`);
    if (c.note) lines.push(`    ${c.note}`);
    lines.push("");
  });

  lines.push("LIMITS & SUPPORT");
  lines.push(rule());
  lines.push("Time limit");
  if (h.provisioned_at)
    lines.push(`  Created:    ${fmtTime(h.provisioned_at)}`);
  lines.push(`  Expires at: ${fmtTime(h.expires_at)}`);
  lines.push("  After expiry the environment and all of its data are deleted.");
  lines.push("");
  lines.push("Performance limit");
  lines.push(
    "  The system is sized for the data delivered to it at provision time.",
  );
  lines.push(
    "  If you experience difficulty accessing or analyzing the data — slow",
  );
  lines.push(
    "  queries, dashboards that take a long time to load, or Discover hangs —",
  );
  lines.push(
    "  contact us and we may be able to upgrade the cluster to provide better",
  );
  lines.push("  throughput.");
  lines.push("");
  lines.push(`Contact: ${h.support.contact}`);
  lines.push("");

  // Dataset configuration appendix — what filters the source export
  // was submitted with. Tells the customer what's IN their env;
  // schema below tells them HOW it's shaped.
  if (h.dataset_configuration) {
    lines.push("APPENDIX · DATASET CONFIGURATION");
    lines.push(rule());
    lines.push("Filters applied to the source export that produced this dataset.");
    lines.push("");
    lines.push("  Input filter (paim filter language)");
    lines.push(`    ${h.dataset_configuration.input_filter || "(no input filter)"}`);
    lines.push("");
    lines.push("  Lucene filter (OpenSearch query string)");
    lines.push(`    ${h.dataset_configuration.lucene_filter || "(no lucene filter)"}`);
    lines.push("");
  }

  // Schema appendix — reference reading, mirrors the PDF layout.
  lines.push("APPENDIX · DATA SCHEMA");
  lines.push(rule());
  lines.push("Each document is a 'superdoc' — one TV program with full transcript.");
  lines.push("Fields:");
  lines.push("");
  h.schema.forEach((f) => {
    lines.push(`  ${f.name}  (${f.type})`);
    lines.push(`    ${f.description}`);
  });
  lines.push("");

  return lines.join("\n");
}

// --- PDF ------------------------------------------------------------

// Page layout tuned for US-letter LANDSCAPE at jsPDF's default 'pt'
// unit. All measurements in points (1pt = 1/72 inch). Landscape
// gives a wider line so the curl examples + endpoint URL block
// don't have to wrap, and so the schema table reads naturally.
// Constants live up here so a future A4 / portrait / margin tweak
// is a single-file edit.
// Landscape Letter: 11" × 8.5" = 792 × 612 pt.
const PAGE_W = 792;
const PAGE_H = 612;
const MARGIN_X = 54;
const BODY_TOP = 84;
const BODY_BOTTOM = PAGE_H - 50; // 562
const FOOTER_Y = PAGE_H - 30; // 582

// TVEyes brand palette mirrored from the portal's ThemeProvider so
// the PDF looks like a TVEyes artifact instead of a generic Letter
// page. RGB tuples — jsPDF accepts (r,g,b) on setDrawColor /
// setFillColor / setTextColor.
const COLOR_PRIMARY: [number, number, number] = [40, 109, 178];   // brand blue
const COLOR_SECONDARY: [number, number, number] = [120, 168, 66]; // brand green
const COLOR_INK: [number, number, number] = [4, 36, 65];          // body text
const COLOR_MUTED: [number, number, number] = [120, 130, 145];    // captions / footer
const COLOR_RULE: [number, number, number] = [225, 230, 238];     // section dividers
const COLOR_CALLOUT_BG: [number, number, number] = [238, 245, 252];
const COLOR_CALLOUT_BORDER: [number, number, number] = [180, 205, 232];
const COLOR_CODE_BG: [number, number, number] = [244, 246, 250];

interface PdfState {
  doc: jsPDF;
  y: number;
  page: number;
  envIdLabel: string;
}

// Apply an [r,g,b] tuple to a jsPDF "set color" method.
function applyRgb(setter: (r: number, g: number, b: number) => unknown, rgb: [number, number, number]): void {
  setter(rgb[0], rgb[1], rgb[2]);
}

function pageChrome(state: PdfState, isFirstPage: boolean): void {
  const { doc } = state;

  // Top brand strip — thin colored bar across the page so even printed
  // and shuffled pages stay identifiable as TVEyes artifacts.
  applyRgb(doc.setFillColor.bind(doc), COLOR_PRIMARY);
  doc.rect(0, 0, PAGE_W, 6, "F");

  // Header
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.text("TVEyes • Customer Search Environment", MARGIN_X, 36);
  doc.text(state.envIdLabel, PAGE_W - MARGIN_X, 36, { align: "right" });

  // Footer accent + page number
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, FOOTER_Y - 14, PAGE_W - MARGIN_X, FOOTER_Y - 14);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFontSize(8);
  // Footer text — keeps the paim tooling name internal; customers
  // see a plain "Customer Search Environment Handoff" identifier
  // plus the generation date.
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(
    `Customer Search Environment Handoff · Generated ${today}`,
    MARGIN_X,
    FOOTER_Y,
  );
  doc.text(`Page ${state.page}`, PAGE_W - MARGIN_X, FOOTER_Y, { align: "right" });

  // Reset to body defaults so subsequent draws start clean.
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  applyRgb(doc.setDrawColor.bind(doc), COLOR_INK);
  doc.setLineWidth(0.5);

  if (isFirstPage) {
    drawHero(state);
  }
}

// Page-1 hero: title, primary accent bar, env-level metadata.
function drawHero(state: PdfState): void {
  const { doc } = state;
  // Title
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("Your TVEyes Search Environment", MARGIN_X, 64);
  // Secondary green underline as brand accent
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, 70, 44, 3, "F");
  // Subtitle
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    "A dedicated, time-bounded OpenSearch cluster with your indexed dataset.",
    MARGIN_X,
    90,
  );
  state.y = 110;
}

function ensureSpace(state: PdfState, needed: number): void {
  if (state.y + needed > BODY_BOTTOM) {
    forcePageBreak(state);
  }
}

// Unconditionally advance to a fresh page. Used to start the Appendix
// on its own page (so the customer-facing actionable sections don't
// get a half-rendered schema table tacked onto the same page).
function forcePageBreak(state: PdfState): void {
  state.doc.addPage();
  state.page += 1;
  pageChrome(state, /* isFirstPage */ false);
  state.y = BODY_TOP;
}

// Pre-compute the wrapped line count for a courier 9pt code block
// given its rendered width. Used to estimate the height of a curl
// example before drawing so we can keep the whole example (title +
// code + description) on a single page via ``ensureSpace``.
function codeBlockHeight(doc: jsPDF, text: string): number {
  const lineH = 12;
  const padding = 10;
  const innerWidth = PAGE_W - MARGIN_X * 2 - padding * 2;
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  let lineCount = 0;
  for (const inputLine of text.split("\n")) {
    const wrapped = doc.splitTextToSize(inputLine, innerWidth) as string[];
    lineCount += wrapped.length;
  }
  return lineCount * lineH + padding * 2;
}

// Same idea for a body() call — count wrapped lines at the given
// size so we can pre-reserve vertical space.
function bodyHeight(doc: jsPDF, text: string, size = 10): number {
  doc.setFontSize(size);
  doc.setFont("helvetica", "normal");
  const wrapped = doc.splitTextToSize(text, PAGE_W - MARGIN_X * 2) as string[];
  return wrapped.length * (size + 3);
}

// Section heading — colored title + thin divider rule beneath.
function heading(state: PdfState, text: string): void {
  ensureSpace(state, 36);
  state.y += 18;
  const { doc } = state;
  applyRgb(doc.setTextColor.bind(doc), COLOR_PRIMARY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(text.toUpperCase(), MARGIN_X, state.y);
  state.y += 6;
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(1);
  doc.line(MARGIN_X, state.y, PAGE_W - MARGIN_X, state.y);
  state.y += 14;
  // Reset ink for body text.
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setLineWidth(0.5);
}

interface BodyOpts {
  mono?: boolean;
  size?: number;
  color?: [number, number, number];
  bold?: boolean;
  indent?: number;
}

function body(state: PdfState, text: string, opts: BodyOpts = {}): void {
  const size = opts.size ?? 10;
  const indent = opts.indent ?? 0;
  const { doc } = state;
  doc.setFont(opts.mono ? "courier" : "helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(size);
  applyRgb(doc.setTextColor.bind(doc), opts.color ?? COLOR_INK);
  const wrapped = doc.splitTextToSize(
    text,
    PAGE_W - MARGIN_X * 2 - indent,
  ) as string[];
  for (const line of wrapped) {
    ensureSpace(state, size + 4);
    doc.text(line, MARGIN_X + indent, state.y);
    state.y += size + 3;
  }
}

// Key/value row used in the env-summary block.
function kvRow(state: PdfState, key: string, value: string): void {
  ensureSpace(state, 16);
  const { doc } = state;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.text(key, MARGIN_X, state.y);
  doc.setFont("helvetica", "normal");
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.text(value, MARGIN_X + 130, state.y);
  state.y += 16;
}

// Filled callout block — used for the credentials section so they
// stand out and the customer can find them quickly.
function callout(state: PdfState, lines: { label: string; value: string; mono?: boolean }[]): void {
  const lineH = 16;
  const padding = 14;
  const contentH = lines.length * lineH;
  const blockH = contentH + padding * 2;
  // Reserve enough vertical room that the bottom padding of the box
  // never crosses into the next section.
  ensureSpace(state, blockH + 16);
  const { doc } = state;
  applyRgb(doc.setFillColor.bind(doc), COLOR_CALLOUT_BG);
  applyRgb(doc.setDrawColor.bind(doc), COLOR_CALLOUT_BORDER);
  doc.setLineWidth(0.75);
  doc.roundedRect(
    MARGIN_X,
    state.y,
    PAGE_W - MARGIN_X * 2,
    blockH,
    5,
    5,
    "FD",
  );
  let y = state.y + padding + 11;
  for (const ln of lines) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.text(ln.label.toUpperCase(), MARGIN_X + padding, y);
    doc.setFont(ln.mono ? "courier" : "helvetica", "normal");
    doc.setFontSize(11);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.text(ln.value, MARGIN_X + padding + 80, y);
    y += lineH;
  }
  // 16pt trailing spacing so the next section isn't crowding the
  // bottom of the callout box.
  state.y += blockH + 16;
  // Reset stroke + text defaults.
  applyRgb(doc.setDrawColor.bind(doc), COLOR_INK);
  doc.setLineWidth(0.5);
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
}

// Code block — filled background, monospace, slight padding. Used
// for curl examples and URLs that the customer is meant to copy
// verbatim. Pre-wraps each input line via splitTextToSize so the
// background rectangle's height matches the ACTUAL number of
// rendered lines (a previous version sized the background from
// raw \n-separated lines and let wrapped overflow run outside the
// gray area).
function codeBlock(state: PdfState, text: string): void {
  const lineH = 12;
  const padding = 10;
  const { doc } = state;
  const innerWidth = PAGE_W - MARGIN_X * 2 - padding * 2;

  // Pre-wrap every line at the code-block font so we know the
  // rendered line count BEFORE we draw the background rectangle.
  // splitTextToSize uses the current font metrics, so we set them
  // here too rather than after the fact.
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  const wrappedAll: string[] = [];
  for (const inputLine of text.split("\n")) {
    const wrapped = doc.splitTextToSize(inputLine, innerWidth) as string[];
    for (const w of wrapped) wrappedAll.push(w);
  }
  const blockH = wrappedAll.length * lineH + padding * 2;
  // Reserve generous trailing space so the next paragraph has
  // breathing room under the code block.
  ensureSpace(state, blockH + 14);

  applyRgb(doc.setFillColor.bind(doc), COLOR_CODE_BG);
  doc.roundedRect(
    MARGIN_X,
    state.y,
    PAGE_W - MARGIN_X * 2,
    blockH,
    3,
    3,
    "F",
  );
  let y = state.y + padding + 9;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  for (const line of wrappedAll) {
    doc.text(line, MARGIN_X + padding, y);
    y += lineH;
  }
  state.y += blockH + 14;
}

// 3-column schema table: field name (monospace) / type / description.
// Wraps the description column when long. Header row repeats only on
// the first appearance — subsequent rows continue across pages.
function schemaTable(state: PdfState, rows: SearchEnvHandoff["schema"]): void {
  const colName = MARGIN_X;
  const colType = MARGIN_X + 140;
  const colDesc = MARGIN_X + 230;
  const descWidth = PAGE_W - MARGIN_X - colDesc;
  const { doc } = state;

  // Header row
  ensureSpace(state, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.text("FIELD", colName, state.y);
  doc.text("TYPE", colType, state.y);
  doc.text("DESCRIPTION", colDesc, state.y);
  state.y += 6;
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, state.y, PAGE_W - MARGIN_X, state.y);
  state.y += 8;

  for (const f of rows) {
    const descLines = doc.splitTextToSize(f.description, descWidth) as string[];
    const rowH = Math.max(14, descLines.length * 11 + 4);
    ensureSpace(state, rowH);
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.text(f.name, colName, state.y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.text(f.type, colType, state.y);
    doc.setFontSize(9);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    descLines.forEach((line, i) => {
      doc.text(line, colDesc, state.y + i * 11);
    });
    state.y += rowH;
  }
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
}

export function renderHandoffPdf(h: SearchEnvHandoff): jsPDF {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const state: PdfState = {
    doc,
    y: BODY_TOP,
    page: 1,
    envIdLabel: h.env_id,
  };
  pageChrome(state, /* isFirstPage */ true);

  // --- Env summary block ---
  // Intentionally omits ``instance_class`` (and any other compute
  // shape detail) — the customer-facing handoff shouldn't expose
  // the cluster size. If the customer hits performance limits the
  // Limits & support section invites them to contact us about an
  // upgrade.
  state.y += 4;
  kvRow(state, "Environment id", h.env_id);
  if (h.doc_count !== null && h.doc_count !== undefined)
    kvRow(state, "Documents", h.doc_count.toLocaleString());
  if (h.provisioned_at !== null && h.provisioned_at !== undefined)
    kvRow(state, "Created", fmtTime(h.provisioned_at));
  kvRow(state, "Available until", fmtTime(h.expires_at));

  // --- Credentials (upfront, used by both Browser + API) ---
  // Hoisted above the section headings because the same user/password
  // is used everywhere downstream — easier for the customer to find,
  // and avoids duplicating them inside both sections.
  heading(state, "Credentials");
  body(
    state,
    "The same user + password works for both the browser and the API below.",
    { color: COLOR_MUTED, size: 9 },
  );
  state.y += 6;
  callout(state, [
    { label: "User", value: h.customer_credentials.user, mono: true },
    { label: "Password", value: h.customer_credentials.password, mono: true },
  ]);

  // --- Browser access ---
  heading(state, "Browser access");
  body(state, "Open this URL in any modern browser, then log in:");
  state.y += 2;
  codeBlock(state, h.dashboards.url);
  body(state, h.dashboards.first_login_note);

  // --- MCP access (when this env was provisioned with one) ---
  // Rendered above API access because the LLM-driven workflow is
  // higher-level than direct HTTP querying — most customers reach
  // for MCP first if their client supports it.
  if (h.mcp) {
    heading(state, "MCP access");
    kvRow(state, "Endpoint", h.mcp.endpoint_url);
    kvRow(state, "Transport", h.mcp.transport);
    kvRow(state, "Auth", h.mcp.auth);
    state.y += 4;
    body(state, h.mcp.note, { color: COLOR_MUTED, size: 9 });
  }

  // --- API access ---
  heading(state, "API access");
  kvRow(state, "Endpoint", h.api.endpoint_url);
  kvRow(state, "Index", h.api.index_name);
  kvRow(state, "Auth", h.api.auth);

  // Examples gets its own page so the curl blocks have room to
  // breathe and don't fight the endpoint/index/auth metadata at the
  // top of API access. Title is rendered at section-heading weight
  // (was a subheading) to match the visual hierarchy of the other
  // top-level sections — readers shouldn't mistake "Examples" for a
  // sub-sub-section of "API access".
  forcePageBreak(state);
  heading(state, "API examples");
  body(
    state,
    "Replace <password> with the password from the Credentials section above.",
    { color: COLOR_MUTED, size: 9 },
  );
  state.y += 4;
  h.sample_queries.forEach((q, i) => {
    // Keep the whole example (title + code block + description) on
    // one page. Without this, a curl example whose title lands near
    // the bottom of a page got split across the page break with
    // just the title on page N and the code block on N+1. We
    // estimate the full block height up-front and force a page
    // break if it won't fit.
    const titleH = 14;
    const blockH = codeBlockHeight(doc, q.curl) + 14; // +14 trailing
    const descH = bodyHeight(doc, q.description, 9);
    const exampleH = titleH + blockH + descH + 8;
    if (state.y + exampleH > BODY_BOTTOM) {
      forcePageBreak(state);
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.text(`${i + 1}. ${q.title}`, MARGIN_X, state.y);
    state.y += titleH;
    codeBlock(state, q.curl);
    body(state, q.description, { color: COLOR_MUTED, size: 9 });
    state.y += 8;
  });

  // --- Compatible clients ---
  heading(state, "Compatible clients");
  body(
    state,
    "OpenSearch is wire-compatible with Elasticsearch 7.10. The clients below are first-party; any Elasticsearch 7.10 client should work.",
    { color: COLOR_MUTED, size: 9 },
  );
  state.y += 4;
  h.compatible_clients.forEach((c) => {
    body(state, c.name, { bold: true });
    body(state, `${c.language} — ${c.url}`, { color: COLOR_MUTED, size: 9 });
    if (c.note) body(state, c.note, { color: COLOR_MUTED, size: 9 });
    state.y += 4;
  });

  // --- Limits & support (moved up — relevant to all preceding sections) ---
  // Operator-side TTL-extension talk lives only in internal docs; the
  // customer reads the two limits they actually have visibility into:
  // when the env expires, and that performance is fixed at provision
  // time. Performance-limit copy invites a support conversation rather
  // than exposing the cluster shape.
  heading(state, "Limits & support");

  body(state, "Time limit", { bold: true });
  if (h.provisioned_at !== null && h.provisioned_at !== undefined)
    kvRow(state, "Created", fmtTime(h.provisioned_at));
  kvRow(state, "Expires at", fmtTime(h.expires_at));
  body(
    state,
    "After expiry the environment and all of its indexed data are deleted automatically.",
    { color: COLOR_MUTED, size: 9 },
  );
  state.y += 8;

  body(state, "Performance limit", { bold: true });
  body(
    state,
    "The system is sized for the data delivered to it at provision time, and that compute "
      + "shape is fixed for the lifetime of this environment. If you experience difficulty "
      + "accessing or analyzing the data — slow queries, dashboards that take a long time "
      + "to load, or Discover requests that hang — contact us and we may be able to upgrade "
      + "the cluster to provide better throughput.",
  );
  state.y += 8;

  body(state, `Contact: ${h.support.contact}`, { color: COLOR_PRIMARY, bold: true });

  // --- Appendix: Dataset configuration (filters on the source export) ---
  if (h.dataset_configuration) {
    forcePageBreak(state);
    heading(state, "Appendix · Dataset configuration");
    body(
      state,
      "The filters applied to the source export that produced this dataset.",
      { color: COLOR_MUTED, size: 9 },
    );
    state.y += 12;

    body(state, "Input filter", { bold: true });
    body(
      state,
      "Applied at source-data read time to choose which broadcast pages enter the export.",
      { color: COLOR_MUTED, size: 9 },
    );
    state.y += 4;
    codeBlock(state, h.dataset_configuration.input_filter || "(no input filter)");
    state.y += 12;

    body(state, "Lucene filter", { bold: true });
    body(
      state,
      "OpenSearch query string applied during indexing to scope the dataset further.",
      { color: COLOR_MUTED, size: 9 },
    );
    state.y += 4;
    codeBlock(state, h.dataset_configuration.lucene_filter || "(no lucene filter)");
  }

  // --- Appendix: Data schema (reference reading; pushed to end) ---
  // Forced page break so the schema table starts clean rather than
  // tail-ending whatever section finished above it. The appendix is
  // reference reading, not action items — putting it on its own page
  // keeps the actionable content visually separate.
  forcePageBreak(state);
  heading(state, "Appendix · Data schema");
  body(
    state,
    "Each document is a 'superdoc' — one TV program with its full transcript.",
    { color: COLOR_MUTED, size: 9 },
  );
  state.y += 8;
  schemaTable(state, h.schema);

  return doc;
}
